<script setup lang="ts">
import { ref } from 'vue'
import { useVehicleStore } from '../../stores/vehicleStore'
import { useSearchHistoryStore } from '../../stores/searchHistoryStore'

const vehicleStore = useVehicleStore()
const historyStore = useSearchHistoryStore()
const regInput = ref('')

async function search() {
  const val = regInput.value.trim().toUpperCase()
  if (val.length < 2) return

  await vehicleStore.searchByRegNr(val)

  if (vehicleStore.carDetails && !vehicleStore.searchError) {
    const details = vehicleStore.carDetails
    historyStore.addEntry({
      type: 'regnr',
      query: val,
      carName: `${details.manuName} ${details.modelName} ${details.typeName || ''}`.trim(),
      carId: details.carId,
    })
  }
}

function onHistorySelect(regNr: string) {
  regInput.value = regNr
  search()
}

defineExpose({ onHistorySelect })
</script>

<template>
  <form class="regnr-search" @submit.prevent="search">
    <div class="regnr-search__plate">
      <div class="regnr-search__flag">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 16">
          <rect width="22" height="16" fill="#F02B2D"/>
          <path d="M0,8h22M8,0v16" stroke="#fff" stroke-width="4"/>
          <path d="M0,8h22M8,0v16" stroke="#002868" stroke-width="2"/>
        </svg>
      </div>
      <div class="regnr-search__country">N</div>
    </div>
    <input
      v-model="regInput"
      type="text"
      maxlength="7"
      placeholder="AB 12345"
      class="regnr-search__input"
      :disabled="vehicleStore.isSearching"
    />
    <button
      type="submit"
      class="regnr-search__btn"
      :disabled="vehicleStore.isSearching || regInput.trim().length < 2"
    >
      <template v-if="vehicleStore.isSearching">
        <span class="spinner"></span>
      </template>
      <template v-else>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
      </template>
    </button>
  </form>
</template>

<style scoped lang="scss">
.regnr-search {
  display: flex;
  align-items: stretch;
  border: 2px solid #002868;
  border-radius: 8px;
  overflow: hidden;
  max-width: 400px;
  margin: 0 auto;

  &__plate {
    display: flex;
    align-items: center;
    background: #002868;
    padding: 0 6px;
    gap: 4px;
  }

  &__flag svg {
    width: 18px;
    height: 14px;
  }

  &__country {
    color: white;
    font-weight: bold;
    font-size: 0.85rem;
  }

  &__input {
    flex: 1;
    border: none;
    padding: 12px;
    font-size: 1.1rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    outline: none;
    min-width: 0;
  }

  &__btn {
    background: #002868;
    color: white;
    border: none;
    padding: 0 16px;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
